<!-- src/views/reviews/UserQrCodeView.vue -->
<template>
  <div class="min-h-screen bg-[#F0F0F0] font-sans selection:bg-[#20FCF1]/30">
    
    <div class="p-4 sm:p-6 md:p-8 pt-6">
      <div class="max-w-xl mx-auto relative">
        
        <!-- 1. PREMIUM BADGE HEADER -->
        <div class="bg-[#0A1630] rounded-t-[40px] p-10 pb-16 border-b-4 border-[#20FCF1] shadow-2xl text-center relative overflow-hidden">
          <!-- Background Shield Decoration -->
          <div class="absolute top-0 right-0 p-8 opacity-5">
            <QrCodeIcon class="h-32 w-32 text-white" />
          </div>

          <div class="relative z-10 flex flex-col items-center">
            <h1 class="text-3xl font-black text-white uppercase tracking-tight leading-none">Review Me</h1>
            <p class="text-[#20FCF1] font-black uppercase text-[10px] tracking-[0.3em] mt-4">
              Performance Verification Pass
            </p>
          </div>
        </div>

        <!-- 2. FLOATING IDENTITY IMAGE (Overlaps Header and Body) -->
        <div class="relative z-20 -mt-12 flex justify-center">
          <div class="relative group">
            <div class="absolute -inset-1 bg-[#20FCF1] rounded-[32px] blur opacity-25 group-hover:opacity-50 transition duration-1000"></div>
            <div class="relative h-24 w-24 bg-white rounded-[28px] p-1 shadow-2xl overflow-hidden">
              <img 
                v-if="userStore.userProfile?.photoUrl" 
                :src="userStore.userProfile.photoUrl" 
                class="h-full w-full object-cover rounded-[24px]" 
                alt="Operative" 
              />
              <div v-else class="h-full w-full bg-slate-100 flex items-center justify-center text-slate-300">
                <UserIcon class="h-10 w-10" />
              </div>
            </div>
            <!-- Verification Tick -->
            <div class="absolute -bottom-2 -right-2 bg-[#20FCF1] p-1.5 rounded-full border-4 border-white shadow-lg">
              <CheckBadgeIcon class="h-4 w-4 text-[#0A1630]" />
            </div>
          </div>
        </div>

        <!-- 3. SCANNER INTERFACE -->
        <div class="bg-white p-8 sm:p-12 pt-14 rounded-b-[40px] shadow-2xl border-x border-b border-slate-100 mb-12">
          
          <!-- Identity Details -->
          <div class="mb-10 text-center space-y-3">
            <p class="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">Authorised Operative</p>
            <h2 class="text-3xl font-black text-[#0A1630] uppercase tracking-tight">
              {{ userNameDisplay }}
            </h2>
            <div v-if="userStore.userProfile?.sentinelNumber" class="inline-flex items-center gap-3 bg-slate-50 px-5 py-2 rounded-2xl border border-slate-100">
              <span class="text-[9px] font-black uppercase text-[#20FCF1] tracking-widest">PTS Index:</span>
              <span class="text-sm font-black text-[#0A1630] tracking-[0.2em]">{{ userStore.userProfile.sentinelNumber }}</span>
            </div>
          </div>

          <!-- The QR Registry Pass -->
          <div class="relative group max-w-[280px] mx-auto mb-12">
            <div class="absolute -inset-2 bg-[#20FCF1] rounded-[48px] blur-2xl opacity-10 group-hover:opacity-30 transition duration-1000"></div>
            
            <div class="relative bg-white border-2 border-slate-100 rounded-[48px] p-8 shadow-2xl">
              <div v-if="qrValue" class="animate-fadeIn">
                <QrcodeVue 
                  :value="qrValue" 
                  :size="214" 
                  level="H" 
                  render-as="svg"
                  :background="'#ffffff'"
                  :foreground="'#0A1630'"
                  class="mx-auto"
                />
              </div>
              <div v-else class="h-[214px] flex items-center justify-center text-slate-400 animate-pulse">
                <div class="w-10 h-10 border-4 border-slate-100 border-t-[#20FCF1] rounded-full animate-spin"></div>
              </div>

              <!-- Live Scan Pulse -->
              <div class="mt-8 flex items-center justify-center gap-3 border-t border-slate-50 pt-6">
                <div class="w-2.5 h-2.5 bg-[#20FCF1] rounded-full animate-ping"></div>
                <span class="text-[10px] font-black uppercase text-[#0A1630] tracking-[0.3em]">Ready for Scan</span>
              </div>
            </div>
          </div>

          <!-- 4. INTERACTIVE UTILITIES -->
          <div class="space-y-8 no-print">
            <div class="text-center group">
              <p class="text-[9px] font-black uppercase tracking-[0.2em] text-slate-300 mb-3 group-hover:text-[#FD5E00] transition-colors">Direct Registry Link</p>
              <p class="text-[10px] font-bold text-slate-400 break-all bg-slate-50 p-4 rounded-2xl border border-slate-100 shadow-inner">
                {{ qrValue }}
              </p>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
               <button 
                @click="printQr"
                class="flex items-center justify-center gap-3 py-5 bg-[#0A1630] text-white rounded-[24px] text-[10px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all active:scale-95 shadow-xl"
              >
                <PrinterIcon class="h-4 w-4 text-[#20FCF1]" />
                Print Pass
              </button>
              
              <button 
                @click="copyLink"
                :class="copied ? 'bg-[#20FCF1] text-[#0A1630]' : 'bg-white text-[#0A1630] border-2 border-slate-200'"
                class="flex items-center justify-center gap-3 py-5 rounded-[24px] text-[10px] font-black uppercase tracking-widest transition-all active:scale-95 shadow-md"
              >
                <component :is="copied ? CheckBadgeIcon : ClipboardDocumentIcon" class="h-4 w-4" />
                {{ copied ? 'Link Copied' : 'Copy Digital' }}
              </button>
            </div>
          </div>

          <!-- 5. ON-SITE PROTOCOL NOTICE -->
          <div class="mt-12 p-8 bg-slate-50 rounded-[32px] border border-slate-100 shadow-inner">
            <div class="flex items-start gap-4">
                <InformationCircleIcon class="h-5 w-5 text-[#20FCF1] shrink-0" />
                <p class="text-[10px] text-slate-500 leading-relaxed font-bold uppercase tracking-wide">
                  Attention Site Managers: Scan this code using a mobile device to access the secure performance review portal.
                </p>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import QrcodeVue from 'qrcode.vue';
import { auth } from '@/firebaseConfig';
import { useUserStore } from '@/stores/userStore';

// Premium Icons
import { 
  QrCodeIcon, 
  ClipboardDocumentIcon, 
  PrinterIcon, 
  CheckBadgeIcon,
  InformationCircleIcon,
  UserIcon
} from '@heroicons/vue/24/outline';

const userStore = useUserStore();
const qrValue = ref('');
const copied = ref(false);

const userNameDisplay = computed(() => {
    const profile = userStore.userProfile;
    if (!profile) return 'Authorised Operative';
    return `${profile.firstName || ''} ${profile.lastName || ''}`.trim();
});

onMounted(() => {
  const user = auth.currentUser;
  if (user) {
    const baseUrl = window.location.origin;
    qrValue.value = `${baseUrl}/public/review/${user.uid}`;
  }
});

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(qrValue.value);
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 3000);
  } catch (err) {
    console.error('Failed to copy link:', err);
  }
};

const printQr = () => {
  window.print();
};
</script>

<style scoped>
.animate-fadeIn {
  animation: fadeIn 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

@media print {
  .no-print { display: none !important; }
  .min-h-screen { background: white !important; }
  .shadow-2xl, .shadow-xl, .shadow-md, .shadow-sm { box-shadow: none !important; }
}
</style>